import { useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  AppBar,
  Button,
  Dialog,
  DialogContent,
  DialogProps,
  IconButton,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { ExportFile } from "@robin/common";

import { DialogTransition } from "./primitives/DialogTransition";
import { VerticalStack } from "./primitives/Stacks";

export const PatientImportDialog: React.FC<
  DialogProps & {
    error: boolean;
    file?: ExportFile;
    onComplete: (password: string) => void;
    onDiscard: () => void;
  }
> = ({ error, file, onComplete, onDiscard, ...rootProps }) => {
  const { open } = rootProps;
  const { t } = useTranslation();

  const [prevFile, setPrevFile] = useState(file);
  const [password, setPassword] = useState("");

  const onCancel = useCallback(() => {
    setPassword("");
    onDiscard();
  }, [onDiscard]);

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onComplete(password);
    },
    [onComplete, password],
  );

  useEffect(() => {
    if (open) setPassword("");
  }, []);

  useEffect(() => {
    if (file) setPrevFile(file);
  }, [file]);

  const viewFile = file || prevFile;

  if (!viewFile) return null;

  return (
    <Dialog TransitionComponent={DialogTransition} {...rootProps}>
      <AppBar elevation={0} sx={{ position: "relative" }}>
        <Toolbar>
          <Typography sx={{ mr: 2, flex: 1 }} variant="h6" component="div">
            {t("patient.import-title")}
          </Typography>
          <Tooltip title={t("common.close")}>
            <IconButton
              aria-label={`${t("common.close")}`}
              color="inherit"
              edge="end"
              onClick={onCancel}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <Typography color="text.secondary" gutterBottom>
          {t("patient.import-text")}
        </Typography>
        <VerticalStack>
          <form onSubmit={onSubmit}>
            <TextField
              autoFocus
              error={error}
              helperText={error ? t("patient.password-error") : ""}
              inputProps={{ autoComplete: "off" }}
              label={t("patient.password-label")}
              margin="dense"
              onChange={(e) => setPassword(e.target.value)}
              required
              type="password"
              value={password}
            />
            <Button
              disabled={!password}
              fullWidth
              size="large"
              sx={{ mt: 3 }}
              type="submit"
            >
              {t("patient.import-cta")}
            </Button>
          </form>
          <Button fullWidth onClick={onCancel} variant="text">
            {t("common.cancel")}
          </Button>
        </VerticalStack>
      </DialogContent>
    </Dialog>
  );
};
